import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { cleanPhone } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pageContent, setPageContent] = useState({ title: 'Contact Us', content: '' });
  const [pageEnabled, setPageEnabled] = useState(true);
  const [contactInfo, setContactInfo] = useState({
    email: 'support@openhouse.com',
    phone: '+1 (555) 123-4567',
    address: '123 Real Estate Ave\nSuite 100\nCity, State 12345'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const [pageRes, settingsRes] = await Promise.all([
        axios.get(`${API}/pages/contact`),
        axios.get(`${API}/site-settings`)
      ]);
      
      setPageContent(pageRes.data);
      const settings = settingsRes.data;
      const isEnabled = settings.pages_enabled?.contact !== false;
      setPageEnabled(isEnabled);

      if (settings.contact_email || settings.contact_phone || settings.contact_address) {
        setContactInfo({
          email: settings.contact_email || 'support@openhouse.com',
          phone: settings.contact_phone || '+1 (555) 123-4567',
          address: settings.contact_address || '123 Real Estate Ave\nSuite 100\nCity, State 12345'
        });
      }
      
      if (!isEnabled) {
        toast.error('This page is currently unavailable');
      }
    } catch (error) {
      console.error('Failed to fetch page content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Clean phone number before submission
      const cleanedData = {
        ...formData,
        phone: cleanPhone(formData.phone)
      };

      await axios.post(`${API}/contact-submissions`, cleanedData);
      toast.success('Thank you! Your message has been sent successfully.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center py-12">
          <p className="text-lg text-stone-600">Loading...</p>
        </div>
      </PublicLayout>
    );
  }

  if (!pageEnabled) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-stone-700 mb-2">Page Unavailable</h2>
            <p className="text-stone-600">This page is currently disabled.</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-4" style={{fontFamily: '"Playfair Display", serif'}}>
          {pageContent.title}
        </h1>
        
        {pageContent.content && (
          <p className="text-lg text-stone-600 mb-8 whitespace-pre-line">
            {pageContent.content}
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <Card className="bg-white p-8 rounded-2xl card-shadow">
            <h2 className="text-2xl font-bold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
              Send us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
                  Name *
                </Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="h-12"
                  placeholder="Your name"
                  required
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
                  Email *
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
                  Phone
                </Label>
                <PhoneInput
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-12"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
                  Subject
                </Label>
                <Input
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="h-12"
                  placeholder="How can we help?"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
                  Message *
                </Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="min-h-[150px]"
                  placeholder="Tell us more..."
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1A4D2E] text-white rounded-full h-12 text-base hover:bg-[#143D24]"
              >
                <Send size={20} className="mr-2" />
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="bg-white p-8 rounded-2xl card-shadow">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                  <Mail className="text-[#1A4D2E]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">Email</h3>
                  <p className="text-stone-600">{contactInfo.email}</p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-white p-8 rounded-2xl card-shadow">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                  <Phone className="text-[#1A4D2E]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">Phone</h3>
                  <p className="text-stone-600">{contactInfo.phone}</p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-white p-8 rounded-2xl card-shadow">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                  <MapPin className="text-[#1A4D2E]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">Address</h3>
                  <p className="text-stone-600 whitespace-pre-line">{contactInfo.address}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ContactPage;
